import React, { createContext, useContext, useState, useCallback } from 'react';

export interface ModalState {
  isOpen: boolean;
  title: string;
  content: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOutsideClick?: boolean;
  showCloseButton?: boolean;
}

interface ModalContextValue {
  // Current modal state
  modal: ModalState | null;
  
  // Actions
  openModal: (modal: Omit<ModalState, 'isOpen'>) => void;
  closeModal: () => void;
  
  // Game-specific modal helpers
  openGamePopup: (gameId: string, gameTitle: string) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalProviderProps {
  children: React.ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modal, setModal] = useState<ModalState | null>(null);

  const openModal = useCallback((modalData: Omit<ModalState, 'isOpen'>) => {
    setModal({
      ...modalData,
      isOpen: true,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const openGamePopup = useCallback((gameId: string, gameTitle: string) => {
    // This will be used later when we create the GamePopup component
    // For now, it's a placeholder that shows the game ID and title
    openModal({
      title: gameTitle,
      content: (
        <div className="space-y-4">
          <p className="text-muted-foreground">Game ID: {gameId}</p>
          <p className="text-sm text-muted-foreground">
            Game popup content will be implemented in subtask 3.2
          </p>
        </div>
      ),
      size: 'lg',
    });
  }, [openModal]);

  const value: ModalContextValue = {
    modal,
    openModal,
    closeModal,
    openGamePopup,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}